import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import { radarFunctionErrorMessage } from "./errors";

export interface RadarSource {
  source_key: string;
  display_name: string;
  source_class: string;
  intake_mode: string;
  authority: string;
  enabled: boolean;
  outbound_gate: boolean;
  default_freshness_days: number;
  expected_cadence_hours: number | null;
  updated_at: string;
  latest_load: {
    status: string;
    record_count: number;
    received_at: string | null;
    committed_at: string | null;
    rejection_reason: string | null;
    metadata: Record<string, unknown>;
  } | null;
}
export interface RadarPursuit {
  pursuit_id: string;
  organization_id: string;
  originating_opportunity_id: string;
  stage: "claimed" | "outreach" | "meeting" | "proposal" | "won" | "closed_lost";
  status: "active" | "won" | "closed_lost" | "archived";
  follow_up_at: string | null;
  lead_email: string | null;
  lead_name: string | null;
  lead_resolution_state: "none" | "needed" | "resolved";
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
  archived_by_email?: string | null;
  archive_evidence?: Array<{
    record_id: string;
    record_kind: string;
    title: string;
    summary: string | null;
    practice_key: string | null;
    source_url: string | null;
    observed_at: string;
  }>;
  proofs: Array<{
    key: string;
    label: string;
    url: string | null;
    suffixes: string[];
    context: string | null;
  }>;
  organizations?: { legal_name?: string | null } | null;
  radar_pursuit_members?: Array<{
    member_email: string;
    member_name: string;
    member_role: string;
    joined_at: string;
    left_at: string | null;
    is_muted: boolean;
  }>;
  radar_pursuit_events?: Array<{
    event_id: string;
    actor_email: string;
    event_type: string;
    body: string | null;
    event_payload: Record<string, unknown>;
    created_at: string;
  }>;
}

export interface RadarTrackedSignal {
  signal_id: string;
  feed_title: string;
  feed_summary: string | null;
  signal_type: string;
  signal_source: string;
  scf_score: number | null;
  last_observed_at: string;
  signal_url: string | null;
}

export interface RadarCompanyTracker {
  tracker_id: string;
  organization_id: string | null;
  company_name: string;
  status: "active" | "archived";
  identity_state: "resolved" | "review_required" | "queued";
  identity_request_id?: string | null;
  identity_note?: string | null;
  signal_count: number;
  recent_signals: RadarTrackedSignal[];
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface RadarAlertOpportunity {
  alert_recipient_id: string;
  alert_id: string;
  opportunity_id: string;
  organization_id: string;
  company_name: string;
  industry: string | null;
  geography: string | null;
  practice_key: string;
  band: "strong" | "developing";
  reasons: Array<string | { reason?: string }>;
  summary: string;
  first_move: string | null;
  evidence_count: number;
  independent_source_count: number;
  state: string;
  snoozed_until: string | null;
  expires_at: string;
  created_at: string;
  dto_version: string;
  pursuit_id: string | null;
}

export interface RadarDistributionMember {
  distribution_member_id: string;
  practice_key: string;
  email: string;
  display_name: string;
  title: string | null;
  is_initial_recipient: boolean;
  is_active: boolean;
}

export interface RadarIdentityCandidate {
  organization_id: string;
  display_name: string;
  match_source: string;
  similarity_score: number;
}

export interface RadarIdentityRequest {
  identity_request_id: string;
  tracker_id: string;
  requested_name: string;
  state: "queued" | "review_required";
  match_basis: string | null;
  confidence_score: number | null;
  candidate_snapshot: RadarIdentityCandidate[];
  resolution_note: string | null;
  company_scan_request_id: string | null;
  created_at: string;
  updated_at: string;
  radar_company_trackers?:
    | { user_email?: string; status?: string }
    | Array<{ user_email?: string; status?: string }>
    | null;
}

export interface RadarStatus {
  enabled: boolean;
  emailEnabled: boolean;
  inboundEmailEnabled: boolean;
  inboundAddress: string | null;
  testMode: boolean;
  practices: string[];
  magicLinkDays: number;
  sources: RadarSource[];
  opportunities: RadarAlertOpportunity[];
  pursuits: RadarPursuit[];
  trackers: RadarCompanyTracker[];
  admin?: {
    distribution: RadarDistributionMember[];
    recentRuns: Array<{
      run_id: string;
      run_kind: string;
      status: string;
      dry_run: boolean;
      created_at: string;
      result_counts: Record<string, number>;
      error_summary: string | null;
    }>;
    pendingNotifications: number;
    readyRecipientCount: number;
    opportunityCount: number;
    scorecardRows: Array<{
      pursuit_id: string;
      stage: string;
      status: string;
      created_at: string;
      updated_at: string;
      radar_opportunities?: { practice_key?: string; qualification_band?: string } | null;
      organizations?: { legal_name?: string } | null;
    }>;
    huddleNotes: Array<{
      huddle_note_id: string;
      practice_key: string;
      week_start: string;
      facilitator_notes: string;
      updated_at: string;
    }>;
    identityQueue: RadarIdentityRequest[];
    archiveGrants: Array<{
      archive_access_grant_id: string;
      user_profile_id: string;
      is_active: boolean;
      created_at: string;
      user_profiles?:
        | { email?: string; display_name?: string | null }
        | Array<{ email?: string; display_name?: string | null }>
        | null;
    }>;
    weekStart: string;
    canConfigure: boolean;
  };
}

export interface RadarCollaborationStatus {
  trackers: RadarCompanyTracker[];
  pursuitState: Array<{
    pursuit_id: string;
    follow_up_at: string | null;
    status: RadarPursuit["status"];
  }>;
  identityQueue: RadarIdentityRequest[];
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(await radarFunctionErrorMessage(name, error));
  const envelope = data as { data?: T; error?: string };
  if (envelope?.error) throw new Error(envelope.error);
  return envelope.data as T;
}

async function fetchRadarCombinedStatus(
  action: "workspace_status" | "status"
): Promise<RadarStatus> {
  const [base, collaboration] = await Promise.all([
    invoke<RadarStatus>("radar_workspace", { action }),
    invoke<RadarCollaborationStatus>("radar_collaboration", { action }),
  ]);
  const stateByPursuit = new Map(collaboration.pursuitState.map((row) => [row.pursuit_id, row]));
  return {
    ...base,
    trackers: collaboration.trackers,
    opportunities: base.opportunities ?? [],
    pursuits: base.pursuits.map((pursuit) => ({
      ...pursuit,
      ...stateByPursuit.get(pursuit.pursuit_id),
    })),
    admin: base.admin
      ? { ...base.admin, identityQueue: collaboration.identityQueue ?? [] }
      : undefined,
  };
}

export async function fetchRadarStatus(): Promise<RadarStatus> {
  const base = await invoke<RadarStatus>("radar_workspace", { action: "workspace_status" });
  return {
    ...base,
    trackers: [],
    opportunities: base.opportunities ?? [],
    pursuits: base.pursuits ?? [],
  };
}

export function fetchRadarCollaborationStatus(): Promise<RadarCollaborationStatus> {
  return invoke<RadarCollaborationStatus>("radar_collaboration", {
    action: "workspace_status",
  });
}

export function fetchRadarPursuitProofs(): Promise<
  Array<{ pursuit_id: string; proofs: RadarPursuit["proofs"] }>
> {
  return invoke("radar_workspace", { action: "pursuit_proofs" });
}

export function fetchRadarAdminStatus(): Promise<RadarStatus> {
  return fetchRadarCombinedStatus("status");
}

export interface RadarArchives {
  pursuits: RadarPursuit[];
  scope: "all" | "my";
  search: string;
  retentionDays: number | null;
  canReadHistorical: boolean;
}

export function fetchRadarArchives(scope: "all" | "my", search: string): Promise<RadarArchives> {
  return invoke("radar_workspace", { action: "archives", scope, search });
}

export function updateRadarPursuit(
  pursuitId: string,
  stage: RadarPursuit["stage"]
): Promise<{ pursuitId: string; stage: string }> {
  return invoke("radar_workspace", { action: "update_pursuit", pursuitId, stage });
}

export function actOnRadarOpportunity(
  alertRecipientId: string,
  recipientAction: "claim" | "pass" | "snooze" | "not_relevant" | "field_intel",
  fields: Record<string, unknown> = {}
): Promise<{
  saved: boolean;
  state: string;
  snoozedUntil: string | null;
  pursuitId: string | null;
  memberRole: string | null;
  leadRequestPending: boolean;
  fieldIntelRecordId?: string | null;
}> {
  return invoke("radar_workspace", {
    action: "recipient_action",
    alertRecipientId,
    recipientAction,
    ...fields,
  });
}

export function actInRadarPursuit(
  pursuitId: string,
  roomAction: "comment" | "mute" | "leave" | "resolve_lead" | "invite" | "follow_up" | "archive",
  fields: Record<string, unknown> = {}
): Promise<Record<string, unknown>> {
  const collaborationAction =
    roomAction === "invite" || roomAction === "follow_up" || roomAction === "archive";
  return invoke(collaborationAction ? "radar_collaboration" : "radar_workspace", {
    action: collaborationAction ? "pursuit_action" : "pursuit_room",
    pursuitId,
    roomAction,
    ...fields,
  });
}

export function fetchRadarTrackingStatus(input: {
  organizationId?: string | null;
  companyName: string;
}): Promise<Pick<
  RadarCompanyTracker,
  | "tracker_id"
  | "organization_id"
  | "company_name"
  | "status"
  | "identity_state"
  | "identity_request_id"
  | "identity_note"
  | "created_at"
  | "updated_at"
  | "archived_at"
> | null> {
  return invoke("radar_collaboration", { action: "tracking_status", ...input });
}

export function trackCompanyInRadar(input: {
  organizationId?: string | null;
  companyName: string;
}): Promise<RadarCompanyTracker> {
  return invoke("radar_collaboration", { action: "track_company", ...input });
}

export function archiveRadarTracker(
  trackerId: string
): Promise<{ trackerId: string; archived: boolean }> {
  return invoke("radar_collaboration", { action: "archive_tracker", trackerId });
}

export function recheckRadarIdentityRequest(
  identityRequestId: string
): Promise<{ identityRequestId: string; state: string; organizationId: string | null }> {
  return invoke("radar_collaboration", { action: "recheck_identity_request", identityRequestId });
}

export function resolveRadarIdentityRequest(
  identityRequestId: string,
  organizationId: string
): Promise<{ identityRequestId: string; organizationId: string; trackerId: string }> {
  return invoke("radar_collaboration", {
    action: "resolve_identity_request",
    identityRequestId,
    organizationId,
  });
}

export function runRadarPreview(): Promise<{
  dryRun: boolean;
  candidateCount: number;
  qualifiedCount: number;
  opportunities: Array<Record<string, unknown>>;
}> {
  return invoke("radar_workspace", { action: "run", dryRun: true });
}

export function runRadarNow(): Promise<{
  dryRun: boolean;
  runId: string;
  qualifiedCount: number;
  alertCount: number;
  recipientCount: number;
}> {
  return invoke("radar_workspace", { action: "run", dryRun: false });
}

export function queueRadarReadyAlerts(): Promise<{
  queuedMessages: number;
  queuedRecipients: number;
  skippedMessages: number;
}> {
  return invoke("radar_workspace", { action: "queue_ready_alerts" });
}

export function dispatchRadarEmails(): Promise<{
  considered: number;
  sent: number;
  failed: number;
  disabled?: boolean;
}> {
  return invoke("radar_dispatch", {});
}

export function saveRadarHuddle(
  practiceKey: string,
  weekStart: string,
  notes: string
): Promise<{ saved: boolean }> {
  return invoke("radar_workspace", { action: "save_huddle", practiceKey, weekStart, notes });
}

export function updateRadarSettings(
  settings: Record<string, unknown>
): Promise<{ updated: boolean }> {
  return invoke("radar_workspace", { action: "update_settings", settings });
}

export function setRadarArchiveAccess(
  email: string,
  enabled: boolean
): Promise<{ email: string; displayName: string; enabled: boolean }> {
  return invoke("radar_workspace", { action: "set_archive_access", email, enabled });
}

export interface RadarFileIntakePayload {
  sourceKey: string;
  file: File;
  observedAt: string;
}

export interface RadarFileIntakePreview {
  source: { source_key: string; display_name: string; enabled: boolean };
  valid: boolean;
  rowCount: number;
  duplicateExternalKeys: number;
  errors: string[];
  preview: Array<{
    organizationName: string;
    title: string;
    recordKind: string;
    practiceKey: string | null;
  }>;
}

export interface RadarFileIntakeCommit {
  loadId?: string;
  committed?: number;
  matched?: number;
  excludedUnmatched?: number;
  idempotent?: boolean;
  load?: { load_id: string; status: string; record_count: number };
}

async function fileBody(payload: RadarFileIntakePayload): Promise<Record<string, unknown>> {
  if (payload.file.size > 10 * 1024 * 1024) throw new Error("RADAR reports cannot exceed 10 MB.");
  const bytes = new Uint8Array(await payload.file.arrayBuffer());
  const chunks: string[] = [];
  for (let offset = 0; offset < bytes.length; offset += 32_768) {
    chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 32_768)));
  }
  return {
    sourceKey: payload.sourceKey,
    filename: payload.file.name,
    contentType: payload.file.type || "application/octet-stream",
    contentBase64: btoa(chunks.join("")),
    observedAt: payload.observedAt,
  };
}

export async function previewRadarFileIntake(
  payload: RadarFileIntakePayload
): Promise<RadarFileIntakePreview> {
  return invoke("radar_workspace", { action: "preview_file_intake", ...(await fileBody(payload)) });
}

export async function commitRadarFileIntake(
  payload: RadarFileIntakePayload
): Promise<RadarFileIntakeCommit> {
  return invoke("radar_workspace", { action: "commit_file_intake", ...(await fileBody(payload)) });
}

export function updateRadarSource(
  sourceKey: string,
  enabled: boolean
): Promise<{ sourceKey: string; enabled: boolean }> {
  return invoke("radar_workspace", { action: "update_source", sourceKey, enabled });
}

export function upsertRadarDistributionMember(input: {
  practiceKey: string;
  email: string;
  displayName: string;
  title?: string;
}): Promise<RadarDistributionMember> {
  return invoke("radar_distribution", { action: "upsert_distribution_member", ...input });
}

export function removeRadarDistributionMember(
  distributionMemberId: string
): Promise<{ distributionMemberId: string; removed: boolean }> {
  return invoke("radar_distribution", {
    action: "remove_distribution_member",
    distributionMemberId,
  });
}

export interface RadarV2AdminStatus {
  settings: {
    engineVersion: "v1" | "v2_shadow" | "v2";
    shadowEnabled: boolean;
    productionSendEnabled: boolean;
    testMode: boolean;
    emailEnabled: boolean;
    inboundEmailEnabled: boolean;
  };
  releaseGate: Record<string, unknown> | null;
  intakeQueueCount: number;
  reviewQueueCount: number;
  verifications: Array<{
    verification_run_id: string;
    environment: "local" | "snapshot" | "staging" | "production_shadow";
    commit_sha: string;
    test_count: number;
    pass_count: number;
    fail_count: number;
    finished_at: string | null;
  }>;
}

export interface RadarV2IntakeItem {
  load_id: string;
  source_key: string;
  original_filename: string | null;
  sender_email: string | null;
  subject: string | null;
  received_at: string | null;
  status: string;
  detected_report_type: string | null;
  report_type_confidence: string | null;
  detected_practice: string | null;
  practice_confidence: string | null;
  detected_tab: string | null;
  column_mapping: Record<string, unknown>;
  record_count: number;
  expires_at: string | null;
}

export function fetchRadarV2AdminStatus(): Promise<RadarV2AdminStatus> {
  return invoke("radar_v2_engine", { action: "status" });
}

export function previewRadarV2Engine(): Promise<{
  committed: false;
  environment: string;
  summary: Record<string, number | string>;
}> {
  return invoke("radar_v2_engine", { commit: false, environment: "staging" });
}

export function buildRadarV2Shadow(): Promise<{
  committed: true;
  environment: string;
  runId: string;
  summary: Record<string, number>;
}> {
  return invoke("radar_v2_engine", { commit: true, environment: "staging" });
}

export function buildRadarV2DraftBundles(): Promise<{
  weekStart: string;
  sendEnabled: boolean;
  recipientBundles: Array<{ email: string; cards: number; status: string; bundleId: string }>;
}> {
  return invoke("radar_v2_scheduler", {});
}

export function fetchRadarV2IntakeQueue(): Promise<RadarV2IntakeItem[]> {
  return invoke("radar_v2_intake", { action: "list_queue" });
}

export function confirmRadarV2Intake(loadId: string): Promise<Record<string, unknown>> {
  return invoke("radar_v2_intake", { action: "confirm", loadId });
}

export async function stageRadarV2Intake(
  payload: RadarFileIntakePayload
): Promise<Record<string, unknown>> {
  return invoke("radar_v2_intake", { action: "stage_upload", ...(await fileBody(payload)) });
}

export interface RadarPublicOpportunity {
  recipient: { name: string; email: string };
  alert: { subject: string; summary: string; firstMove: string | null };
  opportunity: {
    id: string;
    company: string;
    industry: string | null;
    geography: string | null;
    practice: string;
    band: "strong" | "developing";
    reasons: string[];
    evidenceCount: number;
    independentSourceCount: number;
    relationship: string;
  };
  proofs: Array<{
    key: string;
    label: string;
    url: string | null;
    suffixes: string[];
    context: string | null;
  }>;
  evidence: Array<{
    id: string;
    kind: string;
    title: string;
    summary: string | null;
    url: string | null;
    observedAt: string;
    source: string;
    sourceKey: string;
    isBdseSignal: boolean;
    occurrenceCount?: number;
  }>;
  state: string;
  snoozedUntil: string | null;
  expiresAt: string;
  access: { state: "active" | "needs_activation" | "unprovisioned" };
  allowedActions: string[];
  actionNonce?: string;
  dtoVersion?: "v1" | "v2";
}

async function publicInvoke<T>(token: string, body: Record<string, unknown>): Promise<T> {
  const functionName = token.startsWith("radar_") ? "radar_public" : "radar_v2_public";
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(functionName, { body: { token, ...body } }),
    "RADAR private link request",
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(error.message);
  const envelope = data as { data?: T; error?: string };
  if (envelope?.error) throw new Error(envelope.error);
  return envelope.data as T;
}

export function resolveRadarToken(token: string): Promise<RadarPublicOpportunity> {
  if (token.startsWith("radar_")) return publicInvoke(token, { action: "resolve" });
  return publicInvoke<{
    dto: {
      opportunityId: string;
      organizationName: string;
      practiceKey: string;
      practiceLabel: string;
      band: "strong" | "developing";
      whySignal: string | null;
      suggestedFirstMove: string | null;
      routeReasons: Array<{ reason: string }>;
      evidence: Array<{
        evidenceId: string;
        sourceType: string;
        label: string;
        fact: string;
        sourceUrl: string | null;
        owningPractice: string | null;
        observedAt: string;
      }>;
    };
    recipient: { name: string; email: string };
    company: { name: string; industry: string | null; geography: string | null };
    route: {
      practiceKey: string;
      band: "strong" | "developing";
      reasons: Array<{ reason?: string } | string>;
    };
    state: string;
    snoozedUntil: string | null;
    expiresAt: string;
    actionNonce: string;
    access: { state: "active" | "needs_activation" | "unprovisioned" };
    allowedActions: string[];
  }>(token, { action: "resolve" }).then((result) => {
    const evidenceTypes = new Set(result.dto.evidence.map((item) => item.sourceType));
    return {
      recipient: result.recipient,
      alert: {
        subject: `RADAR: ${result.company.name}`,
        summary: result.dto.whySignal ?? "RADAR opportunity",
        firstMove: result.dto.suggestedFirstMove,
      },
      opportunity: {
        id: result.dto.opportunityId,
        company: result.company.name,
        industry: result.company.industry,
        geography: result.company.geography,
        practice: result.dto.practiceLabel || result.dto.practiceKey,
        band: result.route.band,
        reasons: result.route.reasons.map((reason) =>
          typeof reason === "string" ? reason : String(reason.reason ?? "")
        ),
        evidenceCount: result.dto.evidence.length,
        independentSourceCount: evidenceTypes.size,
        relationship:
          result.dto.evidence.find((item) =>
            ["past_client", "sf_pursuit", "open_project", "target_account"].includes(
              item.sourceType
            )
          )?.label ?? "Governed evidence",
      },
      proofs: [
        {
          key: "v2-why-signal",
          label: result.dto.whySignal ?? "Why this is a RADAR signal",
          url:
            result.dto.evidence.find((item) => item.sourceType === "bdse_signal")?.sourceUrl ??
            null,
          suffixes: [],
          context: result.dto.practiceLabel,
        },
      ],
      evidence: result.dto.evidence.map((item) => ({
        id: item.evidenceId,
        kind: item.sourceType,
        title: item.label,
        summary: item.fact,
        url: item.sourceUrl,
        observedAt: item.observedAt,
        source: item.owningPractice ?? "RADAR",
        sourceKey: item.sourceType,
        isBdseSignal: item.sourceType === "bdse_signal",
      })),
      state: result.state,
      snoozedUntil: result.snoozedUntil,
      expiresAt: result.expiresAt,
      access: result.access,
      allowedActions: result.allowedActions,
      actionNonce: result.actionNonce,
      dtoVersion: "v2" as const,
    };
  });
}

export function actOnRadarToken(
  token: string,
  action: string,
  fields: Record<string, unknown> = {},
  actionNonce?: string
): Promise<Record<string, unknown>> {
  return publicInvoke(token, { action, actionNonce, ...fields });
}
